import React from 'react';
import { cn } from '@/lib/utils';

/**
 * CoRegulate Typography Components
 * 
 * Headings: Humanist sans-serif (Manrope)
 * Body: Inter, comfortable spacing
 * Colors: Deep Warm Grey (#1D6B6B) for headings, Soft Charcoal (#2A8585) for body
 */

/**
 * Heading Component
 * H1-H6 with brand styling
 * Mobile-first responsive sizing
 */
export const Heading = ({
  children,
  className,
  as: Tag = 'h2',
  size, // Override automatic sizing
  weight = 'semibold',
  color = 'primary',
  ...props
}) => {
  // Mobile-first responsive sizing
  const tagSizes = {
    h1: 'text-2xl sm:text-3xl md:text-4xl leading-tight',
    h2: 'text-xl sm:text-2xl md:text-3xl leading-tight',
    h3: 'text-lg sm:text-xl md:text-2xl leading-snug',
    h4: 'text-base sm:text-lg md:text-xl leading-snug',
    h5: 'text-sm sm:text-base md:text-lg leading-normal',
    h6: 'text-sm sm:text-base leading-normal',
  };

  const weightStyles = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const colorStyles = {
    primary: 'text-[#1D6B6B]',
    secondary: 'text-[#2A8585]',
    muted: 'text-[#7D8E95]',
    sage: 'text-[#1D6B6B]',
    rose: 'text-[#FFBB98]',
  };

  return (
    <Tag
      className={cn(
        'font-heading',
        'tracking-tight',
        size || tagSizes[Tag],
        weightStyles[weight],
        colorStyles[color],
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};
Heading.displayName = 'Heading';

/**
 * SubHeading Component
 * Secondary heading with softer styling
 * Mobile-first responsive sizing
 */
export const SubHeading = ({
  children,
  className,
  as: Tag = 'p',
  size = 'md',
  ...props
}) => {
  const sizeStyles = {
    sm: 'text-sm sm:text-base leading-relaxed',
    md: 'text-base sm:text-lg leading-relaxed',
    lg: 'text-lg sm:text-xl leading-relaxed',
  };

  return (
    <Tag
      className={cn(
        'font-heading font-medium',
        'text-[#2A8585]',
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};
SubHeading.displayName = 'SubHeading';

/**
 * Body Text
 * Standard paragraph text
 * Mobile-first responsive sizing
 */
export const BodyText = ({
  children,
  className,
  as: Tag = 'p',
  size = 'md',
  color = 'primary',
  leading = 'relaxed',
  ...props
}) => {
  const sizeStyles = {
    sm: 'text-sm sm:text-base',    // Mobile: sm, Desktop: base
    md: 'text-sm sm:text-base',    // Mobile: sm, Desktop: base
    lg: 'text-base sm:text-lg',    // Mobile: base, Desktop: lg
  };

  const colorStyles = {
    primary: 'text-[#2A8585]',
    secondary: 'text-[#7D8E95]',
    muted: 'text-[#7D8E95]',
  };

  const leadingStyles = {
    tight: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose',
  };

  return (
    <Tag
      className={cn(
        'font-body',
        sizeStyles[size],
        colorStyles[color],
        leadingStyles[leading],
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};
BodyText.displayName = 'BodyText';

/**
 * Small Text
 * Supporting text - mobile-first responsive
 */
export const SmallText = ({
  children,
  className,
  as: Tag = 'span',
  color = 'muted',
  ...props
}) => {
  const colorStyles = {
    primary: 'text-[#2A8585]',
    muted: 'text-[#7D8E95]',
    sage: 'text-[#1D6B6B]',
    rose: 'text-[#FFBB98]',
  };

  return (
    <Tag
      className={cn(
        'font-body text-xs sm:text-sm leading-relaxed',
        colorStyles[color],
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};
SmallText.displayName = 'SmallText';

/**
 * Accent Text
 * Highlighted or emphasized text
 */
export const AccentText = ({
  children,
  className,
  as: Tag = 'span',
  variant = 'sage', // 'sage' | 'lavender' | 'rose' | 'gold'
  weight = 'medium',
  ...props
}) => {
  const variantStyles = {
    sage: 'text-[#1D6B6B]',
    lavender: 'text-[#7D8E95]',
    rose: 'text-[#FFBB98]',
    gold: 'text-[#FFBB98]',
    blue: 'text-[#7D8E95]',
  };

  const weightStyles = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
  };

  return (
    <Tag
      className={cn(
        'font-body',
        variantStyles[variant],
        weightStyles[weight],
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};
AccentText.displayName = 'AccentText';

/**
 * Label Text
 * Form labels and small headers - increased for accessibility
 */
export const LabelText = ({
  children,
  className,
  as: Tag = 'label',
  required = false,
  ...props
}) => {
  return (
    <Tag
      className={cn(
        'block font-body font-medium text-base', // Was text-sm
        'text-[#1D6B6B]',
        'leading-relaxed',
        className
      )}
      {...props}
    >
      {children}
      {required && (
        <span className="ml-1 text-[#FFBB98]">*</span>
      )}
    </Tag>
  );
};
LabelText.displayName = 'LabelText';

/**
 * Quote/Blockquote
 * Styled quote block with increased size
 */
export const Quote = ({
  children,
  className,
  author,
  ...props
}) => {
  return (
    <blockquote
      className={cn(
        'relative pl-6',
        'border-l-4 border-[#7D8E95]',
        'py-3',
        className
      )}
      {...props}
    >
      <p className="font-body text-xl text-[#2A8585] italic leading-[1.7]">
        {children}
      </p>
      {author && (
        <footer className="mt-4 font-body text-base text-[#7D8E95]">
          — {author}
        </footer>
      )}
    </blockquote>
  );
};
Quote.displayName = 'Quote';

/**
 * Action Text
 * Text for grounding steps, large and readable
 * Increased ~20% for somatic tools
 */
export const ActionText = ({
  children,
  className,
  ...props
}) => {
  return (
    <p
      className={cn(
        'font-heading font-medium',
        'text-2xl md:text-3xl',  // Was text-xl md:text-2xl
        'text-[#1D6B6B]',
        'leading-[1.5] text-center',
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
};
ActionText.displayName = 'ActionText';

/**
 * Tagline Text
 * Brand tagline styling - increased for warmth
 */
export const Tagline = ({
  children,
  className,
  size = 'md',
  ...props
}) => {
  const sizeStyles = {
    sm: 'text-base',   // Was text-sm
    md: 'text-lg',     // Was text-base
    lg: 'text-xl',     // Was text-lg
  };

  return (
    <p
      className={cn(
        'font-body italic',
        'text-[#2A8585]',
        'leading-relaxed',
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {children || 'Made for ADHD hearts, shaped by ADHD minds.'}
    </p>
  );
};
Tagline.displayName = 'Tagline';

export default Heading;
